import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import WatchRoundedIcon from "@mui/icons-material/WatchRounded";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { useSessionQuery } from "../../auth";
import { ROUTES } from "../../../shared/utils";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

const WHOOP_CLIENT_ID = "f26943b1-e408-4eb8-9a8f-f59553cf765b";
const WHOOP_REDIRECT_URI = "https://peak4.technology/whoop-callback";
const WHOOP_AUTH_URL = "https://api.prod.whoop.com/oauth/oauth2/auth";
const WHOOP_SCOPES = [
  "offline",
  "read:recovery",
  "read:cycles",
  "read:workout",
  "read:sleep",
  "read:profile",
  "read:body_measurement",
];
const FITBIT_CLIENT_ID = "23TVPQ";
const FITBIT_AUTH_URL = "https://www.fitbit.com/oauth2/authorize";

export const ConnectDevice: React.FC = () => {
  const navigate = useNavigate();
  const { data: session } = useSessionQuery();
  const [isConnecting, setIsConnecting] = useState(false);

  const referenceId = useMemo(() => {
    if (session?.user?.email) return session.user.email;
    if (typeof crypto !== "undefined" && "randomUUID" in crypto)
      return crypto.randomUUID();
    return `peak4-${Date.now()}`;
  }, [session?.user?.email]);

  const handleConnectWhoop = useCallback(() => {
    setIsConnecting(true);

    // generate a secure random state (>=8 chars)
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("whoop_oauth_state", state);

    // Mirror state in a short-lived cookie so backend can validate callback state.
    const cookieDomain = window.location.hostname.endsWith("peak4.technology")
      ? "; Domain=.peak4.technology"
      : "";
    const secureAttr = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `whoop_oauth_state=${encodeURIComponent(
      state
    )}; Path=/; Max-Age=600; SameSite=Lax${secureAttr}${cookieDomain}`;

    const whoopScope = encodeURIComponent(WHOOP_SCOPES.join(" "));
    const authUrl = `${WHOOP_AUTH_URL}?client_id=${WHOOP_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(
      WHOOP_REDIRECT_URI
    )}&scope=${whoopScope}&state=${state}`;

    // open the auth URL in a new tab
    window.location.href = authUrl;
  }, []);

  const handleConnectFitbit = useCallback(() => {
    setIsConnecting(true);

    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("fitbit_oauth_state", state);

    const redirectUri = `${window.location.origin}${ROUTES.FITBIT_CALLBACK}`;
    const scope = encodeURIComponent(
      [
        "activity",
        "cardio_fitness",
        "electrocardiogram",
        "heartrate",
        "irregular_rhythm_notifications",
        "location",
        "nutrition",
        "oxygen_saturation",
        "profile",
        "respiratory_rate",
        "settings",
        "sleep",
        "social",
        "temperature",
        "weight",
      ].join(" ")
    );
    const authUrl = `${FITBIT_AUTH_URL}?client_id=${FITBIT_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&scope=${scope}&state=${state}`;

    window.location.href = authUrl;
  }, []);

  const handleSkip = useCallback(() => {
    navigate(ROUTES.DASHBOARD);
  }, [navigate]);

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: { md: "md", xs: "100%" },
        mx: "auto",
        height: "100%",
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        borderColor: "divider",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "100vh",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          p: { xs: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={4} sx={{ flexGrow: 1 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={() => navigate(ROUTES.ASSESSMENT)}>
                <ArrowBackRoundedIcon />
              </IconButton>
              <Typography variant="h5" component="h1" fontWeight={600}>
                Connect your wearable device
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Pair your device to unlock your PEAK4 programme ​
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: "divider",
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "primary.light",
                      color: "primary.contrastText",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <WatchRoundedIcon />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Whoop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Seamlessly sync your strain, recovery, and sleep data with
                      PEAK4.
                    </Typography>
                  </Stack>
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  endIcon={isConnecting ? undefined : <ArrowForwardRoundedIcon />}
                  onClick={handleConnectWhoop}
                  disabled={isConnecting}
                >
                  {isConnecting ? "Connecting..." : "Connect Whoop"}
                </Button>
              </Stack>
            </Card>

            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: "divider",
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "secondary.light",
                      color: "secondary.contrastText",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FavoriteIcon />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Fitbit
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sync Fitbit activity, sleep, and heart-rate data.
                    </Typography>
                  </Stack>
                </Stack>

                <Button
                  variant="outlined"
                  size="large"
                  endIcon={isConnecting ? undefined : <ArrowForwardRoundedIcon />}
                  onClick={handleConnectFitbit}
                  disabled={isConnecting}
                >
                  {isConnecting ? "Connecting..." : "Connect Fitbit"}
                </Button>
              </Stack>
            </Card>
          </Stack>

          <Divider flexItem />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Don&apos;t have your device handy? You can skip this step and come
              back later—your progress is saved.
            </Typography>
            <Button
              variant="text"
              color="inherit"
              onClick={handleSkip}
              sx={{ alignSelf: "flex-start", fontWeight: 600 }}
            >
              Skip for now
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};
